import { useEffect, useState } from "react";
import { Sparkles, MessageSquare, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Compact CEO band rendered at the top of Project Board (above tabs).
 * Greets the user, surfaces 4 contextual quick-suggestion chips, and triggers
 * the parent to switch to the CEO tab + auto-send the chip's prompt through
 * the existing chat endpoint.
 *
 * Pure UX layer — does NOT change CEO chat backend logic, agents, or the
 * pipeline. The chip text is forwarded to the existing
 * `POST /api/projects/{id}/chat` endpoint exactly as if the user typed it.
 */
export default function CeoEmbeddedPanel({ project, onAskCeo }) {
    const { user } = useAuth();
    const [agency, setAgency] = useState(null);

    useEffect(() => {
        api.get("/agency").then(({ data }) => setAgency(data)).catch(() => {});
    }, []);

    const name = (user?.name || user?.email?.split("@")[0] || "there").trim();
    const company = (agency?.agency_name || "your studio").trim();
    const suggestions = buildSuggestions(project);

    return (
        <div
            data-testid="ceo-embedded-panel"
            className="mt-4 rounded-sm border border-zinc-900 bg-gradient-to-br from-[#0e0a08] via-[#0a0a0a] to-black p-4 sm:p-5 flex items-start gap-4 ceo-glow"
        >
            <div className="shrink-0 w-10 h-10 rounded-sm border border-[#FF4400]/40 bg-[#FF4400]/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#FF4400]" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="label-micro text-[#FF4400] mb-1">Project Assistant</div>
                <div className="text-sm text-white">
                    Welcome <span className="font-medium">{name}</span>
                    <span className="text-zinc-500"> from </span>
                    <span className="font-medium">{company}</span>
                    <span className="text-zinc-500">.</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1 line-clamp-1">
                    {ceoOneliner(project)}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                    {suggestions.map((s, i) => (
                        <button
                            key={i}
                            type="button"
                            data-testid={`ceo-suggestion-${i}`}
                            onClick={() => onAskCeo && onAskCeo(s.prompt)}
                            className="text-xs px-3 py-1.5 border border-zinc-800 rounded-sm text-zinc-300 hover:border-[#FF4400] hover:text-white transition-colors flex items-center gap-1.5"
                        >
                            <MessageSquare className="w-3 h-3 text-[#FF4400]" />
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>
            <button
                type="button"
                data-testid="ceo-open-chat"
                onClick={() => onAskCeo && onAskCeo(null)}
                className="shrink-0 hidden sm:flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-[#FF4400] transition-colors"
            >
                Open chat <ChevronRight className="w-3 h-3" />
            </button>
        </div>
    );
}

function ceoOneliner(project) {
    const cur = project?.current_stage || "ceo_briefing";
    const map = {
        ceo_briefing: "Brief is in. Ready to kick off the strategy pass whenever you are.",
        competitor_analysis: "Mapping the competitive landscape — review when it lands.",
        brand_dna: "Crafting the Brand DNA layer. Lock the strategy after review.",
        research: "Pulling hyperlocal research signals. Approve to unblock business plan.",
        business_plan: "Building the business plan. Review numbers before concepts.",
        concepts: "Three campaign concepts coming up — pick the one that wins the room.",
        script: "Three script variants drafting — Performance, Emotional, Short Hook.",
        storyboard: "Storyboard shots being staged. Visuals generate automatically.",
        presentation: "Pitch deck assembling — cinematic 18+ slide narrative.",
        prompts: "Prompt library compiling for hand-off to your video tool.",
        qc_review: "QC pass running. Flag any concerns before delivery.",
        post_production: "Post-production package staging.",
        final_delivery: "Final delivery is ready. Share the client review link.",
    };
    return map[cur] || "All stages are accessible — pick any tab to drive the project.";
}

function buildSuggestions(project) {
    const cur = project?.current_stage || "ceo_briefing";
    const stage = project?.stages?.[cur] || {};
    const stageLabel = stage?.label || cur.replaceAll("_", " ");
    const out = [];
    if (cur === "ceo_briefing" || !stage.output) {
        out.push({
            label: "What should I do next?",
            prompt: "What's the most important next move on this project, and which agent should I run?",
        });
    } else if (stage.status === "approved") {
        out.push({
            label: "What should I do next?",
            prompt: `The ${stageLabel} stage is approved. What's the next move and which agent should I run?`,
        });
    } else {
        out.push({
            label: "Brief me on current stage",
            prompt: `Brief me on the current ${stageLabel} stage — what was produced, the strategic angle, and any open risks.`,
        });
    }
    out.push({
        label: "Summarize this project",
        prompt: "Give me a concise executive summary of this project — brief, audience, current stage, what's done, what's next.",
    });
    out.push({
        label: "Check missing inputs",
        prompt: "Audit this project for missing inputs (client brand assets, competitor seeds, geography, pricing). Tell me what to upload before the next agent runs.",
    });
    out.push({
        label: "Show strategy gaps",
        prompt: "Review the strategy so far and call out gaps, weak assumptions, or contradictions across stages. Be blunt.",
    });
    return out;
}
